
import axios from 'axios';
import { URL } from '../tools/ApiRoutes';

// URL Authentication Endpoint

/**
 * Obtener lista de facturas de proyectos
 * @return {Promise} promise
 */
const getInvoicesList = () => {
    const headers = {
        'Content-Type': 'application/json',
    };

    // El interceptor agregará automáticamente login y refreshToken
    return axios.get(`${URL}/proyectos/listaDeFacturas`, {}, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Crear nuevo proyecto con facturas
 * @param {Object} projectData - Datos del proyecto
 * @param {string} projectData.NameProject - Nombre del proyecto
 * @param {string} projectData.Client - Cliente
 * @param {string} projectData.Email - Email del cliente
 * @param {string} projectData.TypeProject - Tipo de proyecto
 * @param {string} projectData.Currency - Moneda
 * @param {string} projectData.Financial - Datos financieros
 * @param {Array} projectData.Invoices - Array de facturas
 * @param {string} projectData.NitSender - NIT del emisor
 * @param {string} projectData.NitReceiver - NIT del receptor
 * @param {string} projectData.CUFE - CUFE de la factura
 * @param {number} projectData.TotalAmount - Monto total
 * @param {number} projectData.TaxAmount - Monto de impuestos
 * @param {string} projectData.InvoiceNumber - Número de factura
 * @return {Promise} promise
 */
const createProject = (projectData) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    // Validar datos requeridos
    const requiredFields = [
        'NameProject', 'Client', 'Email', 'TypeProject',
        'Currency', 'Invoices', 'NitSender',
        'NitReceiver', 'CUFE', 'TotalAmount', 'TaxAmount', 'InvoiceNumber',
        'Proveedor', 'FileUrl'
    ];

    for (const field of requiredFields) {
        if (projectData[field] === null || projectData[field] === undefined) {
            throw new Error(`Campo requerido faltante: ${field}`);
        }

    }

    // Validar que Invoices sea un array
    if (!Array.isArray(projectData.Invoices)) {
        throw new Error('Invoices debe ser un array');
    }

    // Validar estructura de facturas
    projectData.Invoices.forEach((invoice, index) => {
        const requiredInvoiceFields = ['descripcion', 'cantidad', 'precioUnitario', 'totalLinea'];
        for (const field of requiredInvoiceFields) {
            if (invoice[field] === undefined || invoice[field] === null) {
                throw new Error(`Campo requerido faltante en factura ${index}: ${field}`);
            }
        }
    });


    // El interceptor agregará automáticamente login y refreshToken
    return axios.post(`${URL}/proyectos/crearProyecto`, projectData, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Obtener detalle de un proyecto específico
 * @param {number} idProyecto - ID del proyecto a consultar
 * @return {Promise} promise
 */
const getProjectDetail = (idProyecto) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    return axios.get(`${URL}/proyectos/detalleProyecto`, {
        params: { IdProyectoD: idProyecto },
        headers
    })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Obtener detalle completo de un proyecto especifico
 * @param {number} idProyecto - ID del proyecto a consultar
 * @return {Promise} promise
 */
const getProjectCompleteDetail = (idProyecto) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    return axios.get(`${URL}/proyectos/detalleProyectoCompleto`, {
        params: { IdProyecto: idProyecto },
        headers
    })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Obtener informacion sugerida de Intelisis para un articulo por fabricante y categoria
 * @param {string} fabricante - Fabricante del articulo
 * @param {string} categoria - Categoria del articulo
 * @return {Promise} promise
 */
const getTopInfoArt = (fabricante, categoria) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (!fabricante || !categoria) {
        throw new Error('Fabricante y Categoria son requeridos');
    }

    return axios.get(`${URL}/proyectos/topInfoArt`, {
        params: {
            Fabricante: fabricante,
            Categoria: categoria
        },
        headers
    })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Obtener detalle de artículos de un proyecto por factura
 * @param {string} factura - Número de factura a consultar
 * @return {Promise} promise
 */
const getProjectArticleDetail = (factura, idProyectoD) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    return axios.get(`${URL}/proyectos/detalleArticuloProyecto`, {
        params: { IdFactura: factura, IdProyectoD: idProyectoD },
        headers
    })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Obtener tipos de proyecto disponibles
 * @return {Promise} promise
 */
const getProjectTypes = () => {
    const headers = {
        'Content-Type': 'application/json',
    };

    return axios.get(`${URL}/proyectos/tiposDeProyecto`, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Eliminar una factura del sistema
 * @param {number} idFactura - ID de la factura a eliminar
 * @return {Promise} promise
 */
const deleteInvoice = (idFactura) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const data = { IdFactura: idFactura };

    return axios.delete(`${URL}/proyectos/eliminarFactura`, { data, headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Obtener series de productos
 * @return {Promise} promise
 */
const getSeries = () => {
    const headers = {
        'Content-Type': 'application/json',
    };

    return axios.get(`${URL}/proyectos/obtenerSeries`, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Validar estructura de series
 * @param {Array} series - Array de series a validar
 * @param {number} series[].IdArticulo - ID del artículo
 * @param {string} series[].Serie - Número de serie
 * @return {Promise} promise
 */
const validateSeriesPayload = (series) => {
    // Validar que series sea un array
    if (!Array.isArray(series)) {
        throw new Error('Series debe ser un array');
    }

    // Validar estructura de cada serie
    series.forEach((serie, index) => {
        if (!serie.IdArticulo || !serie.Serie) {
            throw new Error(`Serie ${index}: IdArticulo y Serie son campos requeridos`);
        }
    });
};

/**
 * Crear nuevas series para articulos
 * @param {Array} series - Array de series a crear
 * @param {number} series[].IdArticulo - ID del articulo
 * @param {string} series[].Serie - Numero de serie
 * @return {Promise} promise
 */
const createSeries = (series) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    validateSeriesPayload(series);

    const data = { Series: series };

    return axios.post(`${URL}/proyectos/crearSeries`, data, { headers })
        .then(({ data }) => {
            return data;
        });
};

const validateSeries = (series) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    validateSeriesPayload(series);

    const data = { Series: series };

    return axios.post(`${URL}/proyectos/validarSeries`, data, { headers })
        .then(({ data }) => {
            return data;
        })
        .catch((error) => {
            if (error?.response?.status === 409 && error?.response?.data) {
                return error.response.data;
            }

            throw error;
        });
};

/**
 * Insertar o actualizar precios de articulos del proyecto
 * @param {Array} prices - Array de precios
 * @param {number} prices[].IdArticulo - ID del articulo
 * @param {string} prices[].Articulo - Clave del articulo
 * @param {number} prices[].Precio - Precio del articulo
 * @return {Promise} promise
 */
const insertPrices = (prices) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (!Array.isArray(prices) || prices.length === 0) {
        throw new Error('Prices debe ser un array con al menos un elemento');
    }

    prices.forEach((price, index) => {
        if (!price.IdArticulo || price.Precio === undefined || price.Precio === null) {
            throw new Error(`Price ${index}: IdArticulo y Precio son campos requeridos`);
        }
    });

    const data = { Prices: prices };

    return axios.put(`${URL}/proyectos/insertarPrecio`, data, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Confirmar las facturas y articulos seleccionados del proyecto
 * @param {Object} confirmationData - Datos de confirmacion
 * @param {number} confirmationData.IdProyecto - ID del proyecto
 * @param {number} confirmationData.IdProyectoD - ID del proyecto detalle
 * @param {Array<Object>} confirmationData.Facturas - Facturas a confirmar
 * @param {number} confirmationData.Facturas[].IdFactura - ID de la factura
 * @param {Array<number>} confirmationData.Facturas[].ArticulosSeleccionados - IDs de articulos seleccionados
 * @return {Promise} promise
 */
const confirmProject = (confirmationData) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const requiredFields = ['IdProyecto', 'IdProyectoD', 'Facturas'];

    requiredFields.forEach((field) => {
        if (confirmationData?.[field] === undefined || confirmationData?.[field] === null) {
            throw new Error(`Campo requerido faltante: ${field}`);
        }
    });

    if (!Array.isArray(confirmationData.Facturas) || confirmationData.Facturas.length === 0) {
        throw new Error('Facturas debe ser un array con al menos un elemento');
    }

    confirmationData.Facturas.forEach((factura, index) => {
        if (factura?.IdFactura === undefined || factura?.IdFactura === null) {
            throw new Error(`Factura ${index}: IdFactura es un campo requerido`);
        }

        if (!Array.isArray(factura?.ArticulosSeleccionados) || factura.ArticulosSeleccionados.length === 0) {
            throw new Error(`Factura ${index}: ArticulosSeleccionados debe ser un array con al menos un elemento`);
        }
    });

    return axios.put(`${URL}/proyectos/estadoConfirmado`, confirmationData, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Concluir el proyecto con la informacion final de articulos y series
 * @param {Object} conclusionData - Payload final del proyecto
 * @return {Promise} promise
 */
const concludeProject = (conclusionData) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (!conclusionData || typeof conclusionData !== 'object' || Array.isArray(conclusionData)) {
        throw new Error('conclusionData debe ser un objeto valido');
    }

    return axios.put(`${URL}/proyectos/estadoConcluido`, conclusionData, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Actualizar el estado de un proyecto
 * @param {number} idProyecto - ID del proyecto
 * @param {number} status - Nuevo estado del proyecto
 * @return {Promise} promise
 */
const updateProjectStatus = (idProyecto, status) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const data = {
        IdProyecto: idProyecto,
        Status: status
    };

    return axios.put(`${URL}/proyectos/actualizarEstado`, data, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Actualizar el articulo interno de un proyecto
 * @param {Object} articleData - Datos del articulo
 * @param {number} articleData.IdArticulo - ID del articulo actual
 * @param {string} articleData.Articulo - Clave del articulo nuevo
 * @param {string} articleData.Descripcion1 - Descripcion del articulo nuevo
 * @return {Promise} promise
 */
const actualizarArticuloInterno = (articleData) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    return axios.put(`${URL}/proyectos/actualizarArticuloInterno`, articleData, { headers })
        .then(({ data }) => {
            return data;
        });
};

/**
 * Remover una factura de un proyecto (sin eliminarla del sistema)
 * @param {number} idFactura - ID de la factura a remover
 * @return {Promise} promise
 */
const removeInvoice = (idFactura) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const data = { IdFactura: idFactura };

    return axios.put(`${URL}/proyectos/removerFactura`, data, { headers })
        .then(({ data }) => {
            return data;
        });
};

export const ProjectsService = {
    getInvoicesList,
    createProject,
    getProjectDetail,
    getProjectCompleteDetail,
    getTopInfoArt,
    getProjectArticleDetail,
    getProjectTypes,
    deleteInvoice,
    getSeries,
    validateSeries,
    createSeries,
    insertPrices,
    confirmProject,
    concludeProject,
    updateProjectStatus,
    actualizarArticuloInterno,
    removeInvoice,
};
